import jsPDF from "jspdf";
import { formatCurrency } from "@/lib/currency/currency";
import { getSystemLocale } from "@/lib/locale/locale";
import {
  Order,
  OrderableType,
  BookingType,
  displayStatus,
  OrderItemDayPass,
  OrderItemHourlyPass,
  OrderItemMembership,
  LocatableType,
  AdditionalChargeTransaction,
  AdditionalRefundTransaction,
} from "@/fm/data/orders";
import { getAllOrderFeeLines } from "@/fm/utils/get_all_fee_lines";
import { getAddOnsBreakdownFromOrderItems } from "@/fm/utils/get_add_ons_breakdown";
import { netTotalAfterAdjustments } from "@/fm/utils/net_total_after_adjustments";
import { registerFonts, getFontForText } from "@/fm/utils/pdf_fonts";
import { MembershipCharge } from "@/offering/portal/hooks/use_membership_charges_query";
import { TaxRegistration } from "@/data/location";

interface RecurringChargeContext {
  charge: MembershipCharge;
  membershipName: string;
}

interface ReceiptData {
  order: Order;
  partnerName?: string;
  locationAddress?: string;
  spaceName?: string;
  // Present when the location's receipts are issued by a registered entity,
  // which turns the receipt into a tax receipt under that entity's name.
  taxRegistration?: TaxRegistration | null;
  recurringCharge?: RecurringChargeContext;
  // Chain-aggregated adhoc adjustments — rendered after Total so the receipt
  // reconciles to the same Net total as the order detail breakdown.
  additionalChargeTransactions?: AdditionalChargeTransaction[];
  additionalRefundTransactions?: AdditionalRefundTransaction[];
}

const PAGE_WIDTH = 210;
const MARGIN_LEFT = 20;
const MARGIN_RIGHT = 20;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

export async function downloadOrderReceiptPdf(data: ReceiptData) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  await registerFonts(doc);
  let y = 20;

  const { order, recurringCharge } = data;
  const charge = recurringCharge?.charge;
  const currency =
    charge?.currency || order.currencyCode || order.currency || "USD";
  const locale = getSystemLocale();

  const fmt = (amount: number) => formatCurrency(amount, currency, locale, 2);

  const paymentDateIso = charge?.paymentDate?.slice(0, 10);

  function addPage() {
    doc.addPage();
    y = 20;
  }

  function checkPageBreak(needed: number) {
    if (y + needed > 275) {
      addPage();
    }
  }

  function drawLine() {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_LEFT, y, PAGE_WIDTH - MARGIN_RIGHT, y);
    y += 4;
  }

  function addLabel(text: string) {
    doc.setFont(getFontForText(text), "normal");
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.text(text, MARGIN_LEFT, y);
    y += 4;
  }

  function addText(
    text: string,
    options?: { bold?: boolean; indent?: number }
  ) {
    doc.setFont(getFontForText(text), options?.bold ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    const x = MARGIN_LEFT + (options?.indent ?? 0);
    const lines = doc.splitTextToSize(
      text,
      CONTENT_WIDTH - (options?.indent ?? 0)
    );
    checkPageBreak(lines.length * 4);
    doc.text(lines, x, y);
    y += lines.length * 4;
  }

  function addRow(label: string, value: string, options?: { bold?: boolean }) {
    doc.setFont(getFontForText(label), "normal");
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    doc.text(label, MARGIN_LEFT, y);
    doc.setFont(getFontForText(value), options?.bold ? "bold" : "normal");
    doc.text(value, PAGE_WIDTH - MARGIN_RIGHT, y, { align: "right" });
    y += 5;
  }

  // --- Header ---
  const { taxRegistration } = data;
  const headerName = taxRegistration?.entityName ?? data.partnerName;
  if (headerName) {
    doc.setFont(getFontForText(headerName), "bold");
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text(headerName, MARGIN_LEFT, y);
    y += 8;
  }

  if (data.locationAddress) {
    doc.setFont(getFontForText(data.locationAddress), "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(data.locationAddress, MARGIN_LEFT, y);
    y += 5;
  } else {
    y += 2;
  }

  if (taxRegistration) {
    const taxNumberText = `${taxRegistration.taxNumberLabel}: ${taxRegistration.taxNumber}`;
    doc.setFont(getFontForText(taxNumberText), "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(taxNumberText, MARGIN_LEFT, y);
    y += 5;
  }

  const receiptPrefix = taxRegistration ? "Tax Receipt" : "Receipt";
  const receiptIdSuffix = paymentDateIso ? `-${paymentDateIso}` : "";
  const receiptText = `${receiptPrefix} #${order.confirmationID}${receiptIdSuffix}`;
  doc.setFont(getFontForText(receiptText), "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(receiptText, MARGIN_LEFT, y);
  const dateForHeader = charge
    ? new Date(`${paymentDateIso}T00:00:00`)
    : order.createdAt;
  const dateStr = dateForHeader.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.setFont(getFontForText(dateStr), "normal");
  doc.text(dateStr, PAGE_WIDTH - MARGIN_RIGHT, y, { align: "right" });
  y += 5;

  const statusText = `Status: ${displayStatus(order.status)}`;
  doc.setFont(getFontForText(statusText), "normal");
  doc.text(statusText, MARGIN_LEFT, y);
  y += 8;

  drawLine();

  // --- Customer Details ---
  const hasCustomer = order.fullName || order.email;
  const hasBilling = order.billingDetails;

  if (hasCustomer || hasBilling) {
    checkPageBreak(30);
    const colWidth = CONTENT_WIDTH / 2;

    if (hasCustomer) {
      addLabel("Customer details");
      if (order.fullName) addText(order.fullName);
      if (order.companyName) addText(order.companyName);
      if (order.email) addText(order.email);
      if (order.phoneNumber) addText(order.phoneNumber);
    }

    if (hasBilling) {
      const billingX = MARGIN_LEFT + colWidth;
      const bd = order.billingDetails!;
      let billingY =
        y -
        (hasCustomer
          ? (1 +
              (order.fullName ? 1 : 0) +
              (order.companyName ? 1 : 0) +
              (order.email ? 1 : 0) +
              (order.phoneNumber ? 1 : 0)) *
            4
          : 0);

      doc.setFont(getFontForText("Billing details"), "normal");
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.text("Billing details", billingX, billingY);
      billingY += 4;

      doc.setFontSize(9);
      doc.setTextColor(40, 40, 40);
      const billingLines: string[] = [];
      if (bd.fullName) billingLines.push(bd.fullName);
      if (bd.streetAddress) billingLines.push(bd.streetAddress);
      if (bd.city || bd.postalCode)
        billingLines.push([bd.postalCode, bd.city].filter(Boolean).join(" "));
      if (bd.state) billingLines.push(bd.state);
      if (bd.country) billingLines.push(bd.country);
      if (bd.tax) billingLines.push(`Tax/VAT: ${bd.tax}`);

      for (const line of billingLines) {
        doc.setFont(getFontForText(line), "normal");
        doc.text(line, billingX, billingY);
        billingY += 4;
      }

      y = Math.max(y, billingY);
    }

    y += 4;
    drawLine();
  }

  // --- Payment Method ---
  // Recurring charge's card always wins (it reflects the card actually used
  // for that cycle). For non-recurring orders, paidThrouTeamsSubscription
  // outranks the stored card.
  checkPageBreak(12);
  addLabel("Payment method");
  if (charge?.cardEnding) {
    addText(`${charge.cardBrand || "Card"} ending in ${charge.cardEnding}`);
  } else if (order.paidThrouTeamsSubscription) {
    addText("Payment confirmed");
  } else if (order.paymentMethodDetails?.card) {
    const orderCard = order.paymentMethodDetails.card;
    addText(`${orderCard.network || "Card"} ending in ${orderCard.last4}`);
  } else {
    addText("N/A");
  }
  y += 4;
  drawLine();

  // Recurring-charge variant uses the same row layout as the order-item branch
  // below (Subtotal / Fees / Tax / Discount / Total), driven from the charge
  // payload instead of order.orderItems. First charges go through the order
  // path directly — they don't reach this branch.
  if (recurringCharge && charge) {
    addText(recurringCharge.membershipName, { bold: true });
    addText(charge.membershipPlan.location || "All Access");

    addRow("Subtotal", fmt(charge.subTotalBeforeDiscount));

    if (charge.feeLines) {
      for (const fee of charge.feeLines) {
        addRow(fee.displayName, fmt(fee.calculatedAmount));
      }
    }

    addRow(charge.taxDescription || "Tax", fmt(charge.taxAmount));

    if (charge.promotionAmount > 0) {
      addRow("Discount", `- ${fmt(charge.promotionAmount)}`);
    }

    y += 2;
    drawLine();
    checkPageBreak(10);
    addRow("Total", fmt(charge.totalPrice), { bold: true });

    if (
      charge.transactionType === "refund" &&
      typeof charge.refundedAmount === "number"
    ) {
      addRow("Refunded", `- ${fmt(Math.abs(charge.refundedAmount))}`);
    }

    doc.save(`receipt-${order.confirmationID}-${paymentDateIso}.pdf`);
    return;
  }

  // --- Line Items ---
  // Each booking/membership renders as a single "<name> … <amount>" row, and
  // add-ons are broken out below via the shared breakdown. There is no
  // per-item "Subtotal" — the whole receipt reconciles to a single Subtotal
  // below, matching the order-detail price breakdown panel.
  const allFeeLines = getAllOrderFeeLines(order);
  const addOnsBreakdown = getAddOnsBreakdownFromOrderItems(order.orderItems);

  for (const item of order.orderItems) {
    if (item.type === OrderableType.Booking) {
      checkPageBreak(30);
      const booking = item as OrderItemDayPass | OrderItemHourlyPass;
      addRow(booking.productType.name, fmt(booking.subTotal));
      if (data.spaceName) addText(data.spaceName, { indent: 4 });

      if (booking.details.type === BookingType.Daily) {
        const details = booking.details;
        const startDate = new Date(details.startDate + "T00:00:00");
        const endDate = new Date(details.endDate + "T00:00:00");
        const dateOpts: Intl.DateTimeFormatOptions = {
          month: "short",
          day: "numeric",
          year: "numeric",
        };
        const dateRange =
          details.startDate === details.endDate
            ? startDate.toLocaleDateString("en-US", dateOpts)
            : `${startDate.toLocaleDateString(
                "en-US",
                dateOpts
              )} - ${endDate.toLocaleDateString("en-US", dateOpts)}`;
        addText(`Booking: ${dateRange}`, { indent: 4 });
        let daysLine = `Days: ${details.days}`;
        if (booking.quantity && booking.quantity > 1) {
          daysLine += `, Quantity: ${booking.quantity}`;
        }
        addText(daysLine, { indent: 4 });
      } else {
        const details = booking.details;
        addText(
          `Booking: ${details.date} (${details.startTime} - ${details.endTime})`,
          { indent: 4 }
        );
        const hours = details.hours ?? 0;
        addText(`Hours: ${hours}`, { indent: 4 });
      }
      y += 2;
    } else if (item.type === OrderableType.Membership) {
      checkPageBreak(20);
      const membership = item as OrderItemMembership;
      const orderable = membership.orderable;
      addRow(orderable?.name ?? "Membership", fmt(membership.subTotal));
      if (orderable) {
        const locName =
          orderable.locatableType === LocatableType.Partner
            ? "All Access"
            : (orderable.locatable as any)?.name ?? "";
        if (locName) addText(locName, { indent: 4 });
      }
      y += 2;
    }
    // Add-ons are aggregated and rendered together below.
  }

  // --- Add-ons (one row each, "<name> x <qty>" → line total) ---
  if (addOnsBreakdown) {
    for (const line of addOnsBreakdown.lines) {
      checkPageBreak(6);
      addRow(line.label, fmt(line.amount));
    }
    y += 2;
  }

  drawLine();

  // --- Promo (reduces the item sum into the Subtotal, mirroring the panel) ---
  if (order.promotionLine) {
    checkPageBreak(6);
    addRow(
      `Promo code (${order.promotionLine.code})`,
      `- ${fmt(Math.abs(order.promotionLine.amount))}`
    );
  }

  // --- Subtotal (single line, sums base booking + all add-ons) ---
  checkPageBreak(6);
  addRow("Subtotal", fmt(order.subTotal));

  // --- Fees ---
  for (const fee of allFeeLines) {
    checkPageBreak(6);
    addRow(fee.displayName, fmt(fee.calculatedAmount));
  }

  // --- Tax ---
  checkPageBreak(6);
  addRow(order.taxLine?.description || "Tax", fmt(order.taxLine?.amount ?? 0));

  y += 2;
  drawLine();

  // --- Total ---
  checkPageBreak(10);
  addRow("Total", fmt(order.totalPrice), { bold: true });

  // --- Additional adhoc adjustments + Net total ---
  const additionalCharges = data.additionalChargeTransactions ?? [];
  const additionalRefunds = data.additionalRefundTransactions ?? [];

  if (additionalCharges.length > 0 || additionalRefunds.length > 0) {
    // Render charge / refund as a booking-style line item: label, then
    // Subtotal / [Fees] / Tax rows. Refund amounts are prefixed with `−` to
    // convey direction without a separate total row. Tax rate label is
    // borrowed from the order — an adhoc adjustment on the same order is
    // taxed at the same rate.
    const renderAdjustment = (
      txn: AdditionalChargeTransaction | AdditionalRefundTransaction,
      fallbackLabel: string,
      negate: boolean
    ) => {
      const signed = (s: string) => (negate ? `- ${s}` : s);
      checkPageBreak(24);
      addText(txn.note || fallbackLabel);
      addRow("Subtotal", signed(fmt(txn.subTotal)));
      y += 2;
      if (txn.feeAmount !== 0) {
        addRow("Fees", signed(fmt(txn.feeAmount)));
      }
      if (txn.taxAmount !== 0) {
        addRow(order.taxLine?.description || "Tax", signed(fmt(txn.taxAmount)));
      }
      y += 2;
    };

    for (const txn of additionalCharges) {
      renderAdjustment(txn, "Additional charge", false);
    }
    for (const txn of additionalRefunds) {
      renderAdjustment(txn, "Partial refund", true);
    }

    y += 2;
    drawLine();
    checkPageBreak(10);
    const netTotal = netTotalAfterAdjustments(
      order.totalPrice,
      additionalCharges,
      additionalRefunds
    );
    addRow("Net total", fmt(netTotal), { bold: true });
  }

  // --- Notes ---
  if (order.additionalNotes) {
    y += 4;
    checkPageBreak(12);
    addLabel("Notes");
    addText(order.additionalNotes);
  }

  // --- Special Instructions (event spaces) ---
  if (order.specialInstructions) {
    y += 4;
    checkPageBreak(12);
    addLabel("Special instructions");
    addText(order.specialInstructions);
  }

  doc.save(`receipt-${order.confirmationID}.pdf`);
}
